// src/App.tsx
declare global {
  interface Window {
    registerServiceWorker?: () => void;
  }
}

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      console.log("Force unregistering service worker");
      registration.unregister();
    });
  });
}

window.registerServiceWorker = () => {
  console.log("Service worker registration blocked");
};

import * as React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/hooks/auth";
import { CartProvider } from "@/hooks/useCart";
import ScrollToTop from "@/components/layout/ScrollToTop";
import { initializeSecurityMonitoring } from "@/utils/securityMonitoring";
import { FloatingCart } from "@/components/cart/FloatingCart";
import MobileBottomNav from "@/components/layout/MobileBottomNav";

// ── Public pages (eager — always needed) ──────────────────────────────────────
import Index from "@/pages/Index";
import RecoveryRedirector from "@/components/auth/RecoveryRedirector";
import PhonePromptManager from "@/components/auth/PhonePromptManager";

// ── Public pages (lazy — not needed for the homepage's first paint) ────────────
const Contact = React.lazy(() => import("@/pages/Contact"));
const Blog = React.lazy(() => import("@/pages/Blog"));
const BlogDetail = React.lazy(() => import("@/pages/BlogDetail"));
const Products = React.lazy(() => import("@/pages/Products"));
const ProductDetail = React.lazy(() => import("@/pages/ProductDetail"));
const Cart = React.lazy(() => import("@/pages/Cart"));
const Login = React.lazy(() => import("@/pages/Login"));
const Register = React.lazy(() => import("@/pages/Register"));
const AboutA = React.lazy(() => import("@/pages/AboutA"));
const About = React.lazy(() => import("@/pages/About"));
const ThankYou = React.lazy(() => import("@/pages/ThankYou"));
const AuthCallback = React.lazy(() => import("@/pages/AuthCallback"));
const ClientRegistration = React.lazy(() => import("@/pages/ClientRegistration"));
const Favorites = React.lazy(() => import("@/pages/Favorites"));
const Account = React.lazy(() => import("@/pages/Account"));
const Orders = React.lazy(() => import("@/pages/Orders"));
const Policies = React.lazy(() => import("@/pages/Policies"));
const PolicyDetail = React.lazy(() => import("@/pages/PolicyDetail"));
const Specials = React.lazy(() => import("@/pages/Specials"));
const NotFound = React.lazy(() => import("@/pages/NotFound"));
const ForgotPassword = React.lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("@/pages/ResetPassword"));
const ReviewSubmissionPage = React.lazy(() => import("@/pages/ReviewSubmission"));
const AgriToolsComingSoon = React.lazy(() => import("@/pages/AgriToolsComingSoon"));

// ── Admin pages (lazy — only loaded when admin route is visited) ───────────────
const UserManagement = React.lazy(() => import("@/pages/UserManagement"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const AdminLogin = React.lazy(() => import("@/pages/AdminLogin"));
const ProductManagement = React.lazy(() => import("@/pages/ProductManagement"));
const BlogManagement = React.lazy(() => import("@/pages/admin/BlogManagement"));
const AdminBlogDashboard = React.lazy(() => import("@/pages/AdminBlogDashboard"));
const NewsletterManagement = React.lazy(() => import("@/pages/admin/NewsletterManagement"));
const ReviewManagement = React.lazy(() => import("@/pages/admin/ReviewManagement"));
const ClientManagement = React.lazy(() => import("@/pages/admin/ClientManagement"));
const VideoManagement = React.lazy(() => import("@/pages/VideoManagement"));
const AdminDeliverySettings = React.lazy(() => import("@/pages/AdminDeliverySettings"));
const AdminDrivers = React.lazy(() => import("@/pages/AdminDrivers"));
const CategoryManagement = React.lazy(() => import("@/pages/CategoryManagement"));
const DocumentationManagement = React.lazy(() => import("@/pages/DocumentationManagement"));
const StockLevelManagement = React.lazy(() => import("@/pages/StockLevelManagement"));
const BulkCategoryAssignment = React.lazy(() => import("@/pages/BulkCategoryAssignment"));
const ProductDescriptions = React.lazy(() => import("@/pages/admin/ProductDescriptions"));
const AdminAnalytics = React.lazy(() => import("@/pages/AdminAnalytics"));
const AdminOrders = React.lazy(() => import("@/pages/AdminOrders"));
const PolicyManagement = React.lazy(() => import("@/pages/PolicyManagement"));
const AboutManagement = React.lazy(() => import("@/pages/AboutManagement"));
const AboutVideoManagement = React.lazy(() => import("@/pages/admin/AboutVideoManagement"));
const HomeVideoManagement = React.lazy(() => import("@/pages/admin/HomeVideoManagement"));
const OutOfStockReport = React.lazy(() => import("@/pages/admin/OutOfStockReport"));
const AdminSettings = React.lazy(() => import("@/pages/AdminSettings"));
const SpecialsManagement = React.lazy(() => import("@/pages/SpecialsManagement"));
const TestDelivery = React.lazy(() => import("@/pages/TestDelivery"));

const ProductRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/products/${id}`} replace />;
};

const queryClient = new QueryClient();
initializeSecurityMonitoring();

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const isCartPage = location.pathname.startsWith("/cart");
  return (
    <div className="pb-16 md:pb-0">
      {children}
      {!isCartPage && <FloatingCart />}
      <MobileBottomNav />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CartProvider>
            <div className="min-h-screen bg-background">
              <ScrollToTop />
              <RecoveryRedirector />
              <PhonePromptManager />
              <MainLayout>
                <React.Suspense
                  fallback={
                    <div className="flex items-center justify-center min-h-screen">
                      <div className="text-gray-400 text-sm">Loading...</div>
                    </div>
                  }
                >
                  <Routes>
                    {/* ── Public ── */}
                    <Route path="/" element={<Index />} />
                    <Route path="/cart/*" element={<Cart />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/about-a" element={<AboutA />} />
                    <Route path="/reviews" element={<ReviewSubmissionPage />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogDetail />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/products/:id" element={<ProductDetail />} />
                    <Route path="/product/:id" element={<ProductRedirect />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/client-registration" element={<ClientRegistration />} />
                    <Route path="/favorites" element={<Favorites />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/profile" element={<Account />} />
                    <Route path="/my-account" element={<Account />} />
                    <Route path="/orders" element={<Orders />} />
                    <Route path="/policies" element={<Policies />} />
                    <Route path="/policy/:slug" element={<PolicyDetail />} />
                    <Route path="/policy/terms-of-service" element={<Navigate to="/policy/terms-and-conditions" replace />} />
                    <Route path="/terms" element={<Navigate to="/policy/terms-and-conditions" replace />} />
                    <Route path="/privacy" element={<Navigate to="/policy/privacy-policy" replace />} />
                    <Route path="/specials" element={<Specials />} />
                    <Route path="/tools" element={<AgriToolsComingSoon />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* ── Admin (lazy loaded) ── */}
                    <Route path="/user-management" element={<UserManagement />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/products" element={<ProductManagement />} />
                    <Route path="/admin/users" element={<UserManagement />} />
                    <Route path="/admin/blog-management" element={<BlogManagement />} />
                    <Route path="/admin/blog-dashboard" element={<AdminBlogDashboard />} />
                    <Route path="/admin/blogs/preview/:id" element={<BlogDetail />} />
                    <Route path="/admin/blogs/edit/:id" element={<BlogDetail />} />
                    <Route path="/admin/newsletter-management" element={<NewsletterManagement />} />
                    <Route path="/admin/reviews" element={<ReviewManagement />} />
                    <Route path="/admin/clients" element={<ClientManagement />} />
                    <Route path="/admin/video-management" element={<VideoManagement />} />
                    <Route path="/admin/delivery-settings" element={<AdminDeliverySettings />} />
                    <Route path="/admin/drivers" element={<AdminDrivers />} />
                    <Route path="/admin/category-management" element={<CategoryManagement />} />
                    <Route path="/admin/documentation" element={<DocumentationManagement />} />
                    <Route path="/admin/stock-levels" element={<StockLevelManagement />} />
                    <Route path="/admin/out-of-stock" element={<OutOfStockReport />} />
                    <Route path="/admin/bulk-category-assignment" element={<BulkCategoryAssignment />} />
                    <Route path="/admin/product-descriptions" element={<ProductDescriptions />} />
                    <Route path="/admin/analytics" element={<AdminAnalytics />} />
                    <Route path="/admin/orders" element={<AdminOrders />} />
                    <Route path="/admin/policy-management" element={<PolicyManagement />} />
                    <Route path="/admin/about-management" element={<AboutManagement />} />
                    <Route path="/admin/about-video-management" element={<AboutVideoManagement />} />
                    <Route path="/admin/home-video-management" element={<HomeVideoManagement />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />
                    <Route path="/admin/specials" element={<SpecialsManagement />} />
                    <Route path="/test-delivery" element={<TestDelivery />} />

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </React.Suspense>
              </MainLayout>
              <Toaster />
            </div>
          </CartProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;

